import React, { useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Group,
  Progress,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { getApiBaseUrl } from "../utils";
import { openapi } from "../../openapi";
import { useAppDispatch } from "../app/hooks";

type UploadResponse = {
  uploaded: {
    blobName: string;
    contentType: string;
  };
  mediaAsset: {
    key: string;
    kind: "VIDEO" | "FILE";
    blobPath: string;
    mimeType?: string;
  };
  message?: string;
};

export const MediaAssetAdminUpload: React.FC = () => {
  const dispatch = useAppDispatch();

  const [showUpload, setShowUpload] = useState(false);
  const [key, setKey] = useState("");
  const [kind, setKind] = useState<"VIDEO" | "FILE">("VIDEO");

  const [isUploading, setIsUploading] = useState(false);

  // progressPercent:
  // - number => prosent (0-100)
  // - null   => ukjent total (viser "pågår")
  const [progressPercent, setProgressPercent] = useState<number | null>(0);

  const [error, setError] = useState<string | undefined>();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);

  const resetProgress = () => setProgressPercent(0);

  const uploadWithProgress = (
    url: string,
    formData: FormData,
    onProgress: (percentOrNull: number | null) => void
  ): Promise<UploadResponse> => {
    return new Promise<UploadResponse>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;

      xhr.open("POST", url, true);
      xhr.withCredentials = true;

      xhr.upload.onprogress = (evt) => {
        if (!evt.lengthComputable) {
          onProgress(null);
          return;
        }
        const percent = Math.round((evt.loaded / evt.total) * 100);
        onProgress(percent);
      };

      xhr.onload = () => {
        try {
          const text = xhr.responseText || "{}";
          const parsed = JSON.parse(text) as UploadResponse;

          if (xhr.status >= 200 && xhr.status < 300) {
            onProgress(100);
            resolve(parsed);
            return;
          }

          reject(new Error(parsed?.message ?? text ?? `Upload failed (${xhr.status})`));
        } catch (err: any) {
          reject(new Error(err?.message ?? "Kunne ikke lese svar fra server."));
        }
      };

      xhr.onerror = () => reject(new Error("Nettverksfeil under opplasting."));
      xhr.onabort = () => reject(new Error("Opplasting avbrutt."));

      xhr.send(formData);
    });
  };

  const handleCancel = () => {
    try {
      xhrRef.current?.abort();
      setError("Opplasting avbrutt.");
    } finally {
      setIsUploading(false);
    }
  };

  const handlePickFile: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.currentTarget.files?.[0];
    e.currentTarget.value = "";
    if (!file) return;

    const keyTrimmed = key.trim();
    if (!keyTrimmed) {
      setError("Du må angi en key.");
      return;
    }

    setError(undefined);
    setIsUploading(true);
    resetProgress();

    try {
      const form = new FormData();
      form.append("file", file);
      form.append("kind", kind);

      const url = `${getApiBaseUrl()}/api/multiplacer/media-assets/${encodeURIComponent(
        keyTrimmed
      )}/upload`;

      const json = await uploadWithProgress(url, form, (p) => {
        setProgressPercent((prev) => {
          if (p === null) return null;
          if (prev === null) return p;
          return Math.max(prev, p);
        });
      });

      // ✅ invalidate RTK Query cache (refetch list)
      dispatch(openapi.util.invalidateTags(["MediaAssets"]));

      setShowUpload(false);
    } catch (err: any) {
      setError(err?.message ?? "Opplasting feilet");
    } finally {
      setIsUploading(false);
      xhrRef.current = null;
    }
  };

  const showProgress = isUploading || (typeof progressPercent === "number" && progressPercent > 0);

  return (
    <Stack gap="sm">
      <Button onClick={() => setShowUpload(true)}>Ny opplasting</Button>

      {showUpload && (
        <Card withBorder radius="md" p="md" shadow="sm">
          {/* Scrollbart innhold */}
          <ScrollArea h={420} type="auto" offsetScrollbars>
            <Stack gap="sm">
              <Text fw={600}>Last opp media asset</Text>

              {error && (
                <Alert color="red" variant="light" title="Feil">
                  {error}
                </Alert>
              )}

              <TextInput
                label="Key"
                value={key}
                onChange={(e) => setKey(e.currentTarget.value)}
                disabled={isUploading}
              />

              <SegmentedControl
                value={kind}
                onChange={(v: any) => setKind(v)}
                data={[
                  { value: "VIDEO", label: "Video" },
                  { value: "FILE", label: "Fil" },
                ]}
                disabled={isUploading}
              />

              <Group justify="space-between">
                <Group>
                  <Button
                    component="label"
                    loading={isUploading}
                    disabled={!key.trim() || isUploading}
                  >
                    Velg fil og last opp
                    <input
                      ref={fileInputRef}
                      type="file"
                      hidden
                      accept={kind === "VIDEO" ? "video/*" : "*"}
                      onChange={handlePickFile}
                    />
                  </Button>

                  {isUploading && (
                    <Button variant="light" onClick={handleCancel}>
                      Avbryt
                    </Button>
                  )}
                </Group>

                <Button
                  variant="subtle"
                  onClick={() => setShowUpload(false)}
                  disabled={isUploading}
                >
                  Lukk
                </Button>
              </Group>

              {showProgress && (
                <>
                  {progressPercent === null ? (
                    <Progress value={100} animated striped />
                  ) : (
                    <Progress value={progressPercent} animated striped />
                  )}

                  <Text size="xs" c="dimmed">
                    {progressPercent === null
                      ? "Laster opp…"
                      : progressPercent >= 100
                        ? "Fullfører…"
                        : `Laster opp… ${progressPercent}%`}
                  </Text>
                </>
              )}
            </Stack>
          </ScrollArea>
        </Card>
      )}
    </Stack>
  );
};
